import { query, mutation } from '@/dex-shared/helpers/graphql';

interface MaterialReference {
  type: 'article' | 'question';
  id: number;
}

const knowledgeNodeProps = `
  id
  name
  fullName
  description
  parentId
  siblingOrder
  nodePath
  ltreeKnowledgesByParentId{
    nodes{
      id
    }
  }`;

const hasChildren = (node: any) => {
  if (
    !node.ltreeKnowledgesByParentId ||
    node.ltreeKnowledgesByParentId.nodes.length > 0
  ) {
    return true;
  }
  return false;
};

export default {
  state: {
    knowledges: [],
    serverCachedKnowledges: [],
    parentNode: {},
    childrenNodes: [],
    childrenNodesWithoutGrandChildren: [],
    childrenNodesWithGrandChildren: [],
  },
  mutations: {
    CLEAN_KNOWLEDGES(state: any) {
      state.knowledges = [];
      state.serverCachedKnowledges = [];
    },
    SET_CHILDREN_NODES(state: any, knowledges: Array<any>) {
      state.childrenNodes = knowledges.map(elem => {
        elem.text = elem.name;
        return elem;
      });
      state.childrenNodesWithGrandChildren = state.childrenNodes.filter(elem =>
        hasChildren(elem),
      );
      state.childrenNodesWithoutGrandChildren = state.childrenNodes.filter(
        elem => !hasChildren(elem),
      );
    },
    SET_PARENT_NODE(state: any, knowledge: any) {
      state.parentNode = knowledge;
    },
    SET_KNOWLEDGES(state: any, knowledges: any) {
      state.knowledges = knowledges;
    },
    SET_CACHED_KNOWLEDGES(state: any, knowledges: any) {
      state.serverCachedKnowledges = knowledges;
    },
  },
  actions: {
    async UPDATE_KNOWLEDGE_ASSOCIATIONS(
      { state, dispatch }: any,
      { type, id }: MaterialReference,
    ) {
      const setDifference = (set1, set2) => {
        const set2Ids = set2.map(elem => elem.id);
        return set1.filter(elem => !set2Ids.includes(elem.id));
      };
      const toBeDeleted = setDifference(
        state.serverCachedKnowledges,
        state.knowledges,
      );
      const toBeCreated = setDifference(
        state.knowledges,
        state.serverCachedKnowledges,
      );

      let toBeDeletedPromise = null;
      if (toBeDeleted.length > 0) {
        toBeDeletedPromise = dispatch(
          'DELETE_KNOWLEDGE_ASSOCIATIONS',
          toBeDeleted,
        );
      }

      let toBeCreatedPromise = null;
      if (toBeCreated.length > 0) {
        toBeCreatedPromise = dispatch('CREATE_KNOWLEDGE_ASSOCIATIONS', {
          knowledges: toBeCreated,
          materialReference: { type, id },
        });
      }

      return Promise.all([toBeDeletedPromise, toBeCreatedPromise]);
    },
    async CREATE_KNOWLEDGE_ASSOCIATIONS(
      { commit }: any,
      { knowledges, materialReference }: any,
    ) {
      let response = await mutation(
        `mutation CreateMultipleMaterialKnowledges($id: Int!) {
          ${knowledges
            .map(
              (
                knowledge: any,
                index: number,
              ) => `${materialReference.type}Knowledge${index}: createLtreeKnowledgeAssociation(input: {
              ltreeKnowledgeAssociation: {
                ltreeKnowledgeId: ${knowledge.id}
                ${materialReference.type}Id: $id
              }
            }) {
              clientMutationId
            }`,
            )
            .join('')}
        }`,
        {
          id: materialReference.id,
        },
      );
    },
    async DELETE_KNOWLEDGE_ASSOCIATIONS({ commit }: any, knowledges: any) {
      let response = await mutation(
        `mutation DeleteMultipleKnowledgeAssociations {
          ${knowledges
            .map(
              ({
                associationId,
              }) => `deleteAssociation${associationId}: deleteLtreeKnowledgeAssociationById (input: {
              id: ${associationId}
            }) {
              clientMutationId
            }`,
            )
            .join('')}
        }`,
      );
    },
    async PULL_KNOWLEDGE_ASSOCIATIONS(
      { commit }: any,
      { type, id }: MaterialReference,
    ) {
      let response = await query(
        `query KnowledgeAssociations($id:Int!) {
          allLtreeKnowledgeAssociations(
            condition: {
              ${type}Id: $id
            }
          ){
            nodes {
              id
              ltreeKnowledgeByLtreeKnowledgeId {
                ${knowledgeNodeProps}
              }
            }
          }
        }`,
        {
          id,
        },
      );
      const knowledges = response.data.allLtreeKnowledgeAssociations.nodes.map(
        elem => {
          return {
            ...elem.ltreeKnowledgeByLtreeKnowledgeId,
            text: elem.ltreeKnowledgeByLtreeKnowledgeId.name,
            associationId: elem.id,
          };
        },
      );
      commit('SET_CACHED_KNOWLEDGES', knowledges);
      commit('SET_KNOWLEDGES', knowledges);
    },
    async PULL_CHILDREN_NODES({ commit }: any, id: number) {
      const results = await query(
        `query getChildrenNodes($id: Int!){
          ltreeKnowledgeById(id: $id){
            ltreeKnowledgesByParentId(orderBy: NAME_ASC){
              nodes{
                ${knowledgeNodeProps}
              }
            }
          }
        }`,
        { id },
      );
      const nodes =
        results.data.ltreeKnowledgeById.ltreeKnowledgesByParentId.nodes;
      commit('SET_CHILDREN_NODES', nodes);
    },
    async PULL_NODE({ commit }: any, id: number) {
      const results = await query(
        `query getNode($id: Int!){
          ltreeKnowledgeById(id: $id){
            ${knowledgeNodeProps}
          }
        }`,
        { id },
      );
      commit('SET_PARENT_NODE', results.data.ltreeKnowledgeById);
    },
    async PULL_ROOT_NODES({ commit }: any) {
      const results = await query(
        `query getAllLtreeKnowledgesDexAdmin{
          allLtreeKnowledges(
            filter: {
              parentId: {
                isNull: true
              }
            },
            orderBy: NAME_ASC
          ){
            nodes{
              ${knowledgeNodeProps}
            }
          }
        }`,
      );
      commit('SET_CHILDREN_NODES', results.data.allLtreeKnowledges.nodes);
      commit('SET_PARENT_NODE', {});
    },
  },
};
